import { FC, useState, useRef, useEffect, DragEvent, ChangeEvent } from 'react';
import {
  UploadCloud,
  FileSpreadsheet,
  CheckCircle2,
  XCircle,
  TrendingUp,
  DollarSign,
  Tag,
  Users,
  Layers,
  Trash2,
  Eye,
  RefreshCw,
} from 'lucide-react';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Input } from '../../ui/Input';
import { Badge } from '../../ui/Badge';
import { Spinner } from '../../ui/Spinner';
import { ConfirmationModal } from '../../ui/ConfirmationModal';
import { DataTable, Column } from '../../widgets/DataTable';
import { MetricWidget } from '../../widgets/MetricWidget';
import { useToast } from '../../ui/Toast';
import { useAuth } from '../../../context/AuthContext';
import {
  uploadReconciliationCsv,
  fetchReconciliationHistory,
  fetchReconciliationDetails,
  deleteReconciliation,
  FinancialReconciliationResponse,
  FinancialReconciliationListItem,
  ReconciliationGroupBreakdown,
  ReconciliationCampaignBreakdown,
  AffiliateConversionResponse,
} from '../../../api/reconciliation';

export const CsvReconciliationTab: FC = () => {
  const { token } = useAuth();
  const { success, error: toastError } = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [marketplace, setMarketplace] = useState<string>('auto');
  const [adSpend, setAdSpend] = useState<string>('0.00');

  const [isUploading, setIsUploading] = useState(false);
  const [currentReconciliation, setCurrentReconciliation] =
    useState<FinancialReconciliationResponse | null>(null);

  const [history, setHistory] = useState<FinancialReconciliationListItem[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [activeSubTab, setActiveSubTab] = useState<
    'groups' | 'campaigns' | 'transactions'
  >('groups');

  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadHistory = async () => {
    if (!token) return;
    setIsLoadingHistory(true);
    try {
      const data = await fetchReconciliationHistory(token);
      setHistory(data);
    } catch {
      // Ignora erro silencioso no carregamento de histórico
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    loadHistory();
  }, [token]);

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const file = e.dataTransfer.files[0];
      if (
        file.name.toLowerCase().endsWith('.csv') ||
        file.name.toLowerCase().endsWith('.txt')
      ) {
        setSelectedFile(file);
      } else {
        toastError(
          'Por favor, selecione um arquivo de texto ou planilha no formato CSV (.csv).',
          'Formato Inválido'
        );
      }
    }
  };

  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleProcessCsv = async () => {
    if (!token) {
      toastError('Você precisa estar autenticado.', 'Sessão Expirada');
      return;
    }
    if (!selectedFile) {
      toastError(
        'Selecione ou arraste um arquivo CSV antes de prosseguir.',
        'Arquivo Ausente'
      );
      return;
    }

    setIsUploading(true);
    try {
      const parsedAdSpend = parseFloat(adSpend) || 0;
      const res = await uploadReconciliationCsv(
        token,
        selectedFile,
        marketplace,
        parsedAdSpend
      );
      setCurrentReconciliation(res);
      success(
        `Conciliação concluída com sucesso! ${res.matched_conversions} de ${res.total_rows} conversões cruzadas.`,
        'Relatório Processado'
      );
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      loadHistory();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toastError(msg, 'Erro no Processamento');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSelectHistoryItem = async (id: string) => {
    if (!token) return;
    try {
      const details = await fetchReconciliationDetails(token, id);
      setCurrentReconciliation(details);
      success('Relatório anterior carregado na visualização.', 'Histórico');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toastError(msg, 'Falha ao Carregar');
    }
  };

  const confirmDeleteReconciliation = async () => {
    if (!token || !itemToDelete) return;
    setIsDeleting(true);
    try {
      await deleteReconciliation(token, itemToDelete);
      success('Registro de conciliação excluído.', 'Removido');
      if (currentReconciliation?.id === itemToDelete) {
        setCurrentReconciliation(null);
      }
      setItemToDelete(null);
      loadHistory();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toastError(msg, 'Erro ao Excluir');
    } finally {
      setIsDeleting(false);
    }
  };

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(val);
  };

  // Colunas: Lucro por Grupo
  const groupColumns: Column<ReconciliationGroupBreakdown>[] = [
    {
      key: 'group_name',
      header: 'Grupo de WhatsApp',
      render: (item) => (
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle text-content-main">
            <Users className="h-4 w-4" />
          </div>
          <div>
            <div className="font-semibold text-content-main">
              {item.group_name}
            </div>
            {item.group_id && (
              <span className="text-2xs text-content-muted">
                ID: {item.group_id}
              </span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'total_conversions',
      header: 'Conversões',
      render: (item) => (
        <span className="font-medium text-content-main">
          {item.total_conversions}
        </span>
      ),
    },
    {
      key: 'confirmed_commission',
      header: 'Comissões Confirmadas',
      render: (item) => (
        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
          {formatCurrency(item.confirmed_commission)}
        </span>
      ),
    },
    {
      key: 'cancelled_amount',
      header: 'Cancelados / Estornos',
      render: (item) => (
        <span className="text-xs text-red-600 dark:text-red-400">
          {item.cancelled_count > 0
            ? `${item.cancelled_count} itens (${formatCurrency(item.cancelled_amount)})`
            : '-'}
        </span>
      ),
    },
    {
      key: 'net_profit',
      header: 'Lucro Líquido Real',
      render: (item) => (
        <span
          className={`font-bold ${
            item.net_profit >= 0
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-red-600 dark:text-red-400'
          }`}
        >
          {formatCurrency(item.net_profit)}
        </span>
      ),
    },
  ];

  // Colunas: Lucro por Campanha
  const campaignColumns: Column<ReconciliationCampaignBreakdown>[] = [
    {
      key: 'campaign_name',
      header: 'Campanha / Fila',
      render: (item) => (
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg border border-surface-border bg-surface-subtle text-content-main">
            <Layers className="h-4 w-4" />
          </div>
          <div className="font-semibold text-content-main">
            {item.campaign_name}
          </div>
        </div>
      ),
    },
    {
      key: 'total_conversions',
      header: 'Vendas',
      render: (item) => (
        <span className="font-medium text-content-main">
          {item.total_conversions}
        </span>
      ),
    },
    {
      key: 'confirmed_commission',
      header: 'Comissões',
      render: (item) => (
        <span className="font-semibold text-emerald-600 dark:text-emerald-400">
          {formatCurrency(item.confirmed_commission)}
        </span>
      ),
    },
    {
      key: 'cancelled_count',
      header: 'Cancelados',
      render: (item) => (
        <span className="text-xs text-red-600 dark:text-red-400">
          {item.cancelled_count > 0
            ? `${item.cancelled_count} cancelamentos`
            : '-'}
        </span>
      ),
    },
    {
      key: 'net_profit',
      header: 'Lucro Líquido Real',
      render: (item) => (
        <span
          className={`font-bold ${
            item.net_profit >= 0
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-red-600 dark:text-red-400'
          }`}
        >
          {formatCurrency(item.net_profit)}
        </span>
      ),
    },
  ];

  // Colunas: Transações Individuais
  const transactionColumns: Column<AffiliateConversionResponse>[] = [
    {
      key: 'conversion_date',
      header: 'Data / ID',
      render: (item) => (
        <div>
          <div className="font-medium text-content-main">
            {item.conversion_date
              ? new Date(item.conversion_date).toLocaleDateString('pt-BR')
              : '-'}
          </div>
          <div className="text-2xs text-content-muted">
            {item.order_id || item.item_sku || '-'}
          </div>
        </div>
      ),
    },
    {
      key: 'subid',
      header: 'SubID Canônico',
      render: (item) => (
        <div className="font-mono text-micro text-brand-600 dark:text-brand-400">
          {item.subid || (
            <span className="italic text-content-muted">Sem SubID</span>
          )}
        </div>
      ),
    },
    {
      key: 'item_title',
      header: 'Item / Produto',
      render: (item) => (
        <div
          className="max-w-xs truncate font-medium text-content-main"
          title={item.item_title || ''}
        >
          {item.item_title || item.item_sku || 'Produto'}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (item) => {
        if (item.status === 'confirmed') {
          return <Badge variant="online">Confirmado</Badge>;
        }
        if (item.status === 'cancelled' || item.status === 'returned') {
          return <Badge variant="offline">Cancelado</Badge>;
        }
        return <Badge variant="warning">Pendente</Badge>;
      },
    },
    {
      key: 'commission_amount',
      header: 'Comissão',
      render: (item) => (
        <span
          className={`font-semibold ${
            item.status === 'confirmed'
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-content-muted line-through'
          }`}
        >
          {formatCurrency(item.commission_amount)}
        </span>
      ),
    },
    {
      key: 'matched',
      header: 'Vínculo do Sistema',
      render: (item) => (
        <div className="space-y-0.5 text-micro">
          {item.matched_group_name ? (
            <div className="flex items-center gap-1 text-content-main">
              <Users className="h-3 w-3 text-brand-500" />
              <span className="max-w-[120px] truncate">
                {item.matched_group_name}
              </span>
            </div>
          ) : (
            <div className="italic text-content-muted">Sem grupo</div>
          )}
          {item.matched_offer_title && (
            <div className="flex items-center gap-1 text-content-muted">
              <Tag className="h-3 w-3 text-amber-500" />
              <span className="max-w-[120px] truncate">
                {item.matched_offer_title}
              </span>
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Cabeçalho do Módulo */}
      <div>
        <h3 className="text-sm font-semibold text-content-main">
          Importação e Conciliação Financeira de CSVs
        </h3>
        <p className="text-xs text-content-muted">
          Cruze os relatórios de comissões exportados da Amazon e Mercado Livre
          com os SubIDs rastreados para auditar o Lucro Líquido Real por grupo e
          campanha.
        </p>
      </div>

      {/* Área de Drag-and-Drop e Formulário de Upload */}
      <Card>
        <Card.Body className="space-y-4 p-5">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-all ${
              dragOver
                ? 'border-brand-500 bg-brand-500/5'
                : 'hover:bg-surface-subtle/50 border-surface-border hover:border-brand-500/50'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={handleFileInputChange}
            />

            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-500/10 text-brand-600 dark:text-brand-400">
              <UploadCloud className="h-6 w-6" />
            </div>

            <div className="mt-3">
              <p className="text-xs font-semibold text-content-main">
                {selectedFile
                  ? selectedFile.name
                  : 'Arraste e solte seu relatório CSV aqui ou clique para selecionar'}
              </p>
              <p className="mt-1 text-2xs text-content-muted">
                Suporta relatórios da Amazon Associados e Mercado Livre
                Afiliados (.csv)
              </p>
            </div>

            {selectedFile && (
              <div className="mt-3 flex items-center gap-2 rounded-lg border border-surface-border bg-surface-panel px-3 py-1.5 text-xs">
                <FileSpreadsheet className="h-4 w-4 text-emerald-500" />
                <span className="font-medium text-content-main">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)}{' '}
                  KB)
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                  }}
                  className="cursor-pointer text-content-muted hover:text-red-500"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Opções de Processamento */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-micro font-medium text-content-muted">
                Formato do Relatório / Marketplace
              </label>
              <select
                value={marketplace}
                onChange={(e) => setMarketplace(e.target.value)}
                className="focus:outline-hidden w-full rounded-lg border border-surface-border bg-surface-panel px-3 py-2 text-xs font-medium text-content-main focus:border-brand-500"
              >
                <option value="auto">
                  Detecção Automática (Reconhece Colunas)
                </option>
                <option value="amazon">
                  Amazon Associados (ASIN & ascsubtag)
                </option>
                <option value="mercadolivre">
                  Mercado Livre Afiliados (MLB & SubID)
                </option>
              </select>
            </div>

            <div>
              <Input
                label="Custos de Tráfego / Anúncios Pagos (R$)"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={adSpend}
                onChange={(e) => setAdSpend(e.target.value)}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="primary"
              onClick={handleProcessCsv}
              isLoading={isUploading}
              disabled={!selectedFile || isUploading}
              leftIcon={<TrendingUp className="h-4 w-4" />}
            >
              {isUploading
                ? 'Reconciliando Transações...'
                : 'Processar e Conciliar CSV'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Indicador de Processamento em Andamento */}
      {isUploading && (
        <Card>
          <Card.Body className="flex flex-col items-center justify-center space-y-3 py-10">
            <Spinner size="lg" color="brand" />
            <div className="text-center">
              <h4 className="text-xs font-semibold text-content-main">
                Cruzando dados de conversão com histórico de disparos
              </h4>
              <p className="text-2xs text-content-muted">
                Analisando SubIDs (`PROJETO_CATEGORIA_GRUPO`), identificando
                ASINs/MLBs e calculando Lucro Líquido Real...
              </p>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Resultados Consolidados */}
      {currentReconciliation && !isUploading && (
        <div className="space-y-6">
          <div className="flex items-center justify-between border-b border-surface-border pb-3">
            <div>
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-content-main">
                  Resultado Consolidado: {currentReconciliation.filename}
                </h4>
                <Badge variant="online">Processado</Badge>
              </div>
              <p className="text-micro text-content-muted">
                Marketplace: {currentReconciliation.marketplace.toUpperCase()} •
                Importado em:{' '}
                {new Date(currentReconciliation.created_at).toLocaleString(
                  'pt-BR'
                )}
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentReconciliation(null)}
            >
              Fechar Visualização
            </Button>
          </div>

          {/* Grade de KPIs e Métricas de Lucro */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <MetricWidget
              label="Comissões Confirmadas"
              value={formatCurrency(currentReconciliation.total_commission)}
              icon={DollarSign}
              badge={<Badge variant="online">Confirmado</Badge>}
            />
            <MetricWidget
              label="Itens Cancelados"
              value={formatCurrency(currentReconciliation.total_cancelled)}
              icon={XCircle}
              badge={<Badge variant="offline">Estornos</Badge>}
            />
            <MetricWidget
              label="Custo de Tráfego"
              value={formatCurrency(currentReconciliation.ad_spend)}
              icon={TrendingUp}
              badge={<Badge variant="neutral">Meta Ads</Badge>}
            />
            <MetricWidget
              label="Lucro Líquido Real"
              value={formatCurrency(currentReconciliation.net_profit)}
              icon={CheckCircle2}
              badge={
                <Badge
                  variant={
                    currentReconciliation.net_profit >= 0 ? 'online' : 'danger'
                  }
                >
                  {currentReconciliation.net_profit >= 0
                    ? 'Positivo'
                    : 'Prejuízo'}
                </Badge>
              }
            />
            <MetricWidget
              label="Taxa de Cruzamento"
              value={`${currentReconciliation.summary.match_rate_percent}%`}
              description={`${currentReconciliation.matched_conversions} de ${currentReconciliation.total_rows} cruzados`}
              icon={Layers}
              badge={<Badge variant="cadence">SubIDs</Badge>}
            />
          </div>

          {/* Sub-tabs de Detalhamento */}
          <div className="space-y-4">
            <div className="border-b border-surface-border">
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setActiveSubTab('groups')}
                  className={`cursor-pointer rounded-t-lg border-b-2 px-4 py-2 text-xs font-semibold transition-colors ${
                    activeSubTab === 'groups'
                      ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                      : 'border-transparent text-content-muted hover:text-content-main'
                  }`}
                >
                  Lucro por Grupo WhatsApp (
                  {currentReconciliation.summary.by_group.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSubTab('campaigns')}
                  className={`cursor-pointer rounded-t-lg border-b-2 px-4 py-2 text-xs font-semibold transition-colors ${
                    activeSubTab === 'campaigns'
                      ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                      : 'border-transparent text-content-muted hover:text-content-main'
                  }`}
                >
                  Lucro por Campanha / Fila (
                  {currentReconciliation.summary.by_campaign.length})
                </button>
                <button
                  type="button"
                  onClick={() => setActiveSubTab('transactions')}
                  className={`cursor-pointer rounded-t-lg border-b-2 px-4 py-2 text-xs font-semibold transition-colors ${
                    activeSubTab === 'transactions'
                      ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                      : 'border-transparent text-content-muted hover:text-content-main'
                  }`}
                >
                  Transações Reconciliadas (
                  {currentReconciliation.conversions.length})
                </button>
              </div>
            </div>

            {/* Renderização da Tabela Selecionada */}
            {activeSubTab === 'groups' && (
              <DataTable
                columns={groupColumns}
                data={currentReconciliation.summary.by_group}
                keyExtractor={(item) => item.group_name}
                emptyMessage="Nenhuma conversão associada a grupos de WhatsApp nesta planilha."
                pageSize={10}
              />
            )}

            {activeSubTab === 'campaigns' && (
              <DataTable
                columns={campaignColumns}
                data={currentReconciliation.summary.by_campaign}
                keyExtractor={(item) => item.campaign_name}
                emptyMessage="Nenhuma conversão associada a campanhas ou filas nesta planilha."
                pageSize={10}
              />
            )}

            {activeSubTab === 'transactions' && (
              <DataTable
                columns={transactionColumns}
                data={currentReconciliation.conversions}
                keyExtractor={(item) => item.id}
                emptyMessage="Nenhuma transação individual encontrada."
                pageSize={15}
              />
            )}
          </div>
        </div>
      )}

      {/* Histórico de Conciliações Anteriores */}
      <Card>
        <Card.Header
          title="Histórico de Conciliações Financeiras"
          description="Lotes e planilhas processados anteriormente no canal"
          action={
            <Button
              variant="ghost"
              size="sm"
              onClick={loadHistory}
              leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
            >
              Atualizar
            </Button>
          }
        />
        <Card.Body className="p-0">
          {isLoadingHistory ? (
            <div className="flex justify-center py-8">
              <Spinner size="md" color="brand" />
            </div>
          ) : history.length === 0 ? (
            <div className="py-8 text-center text-xs text-content-muted">
              Nenhum relatório de conciliação importado ainda.
            </div>
          ) : (
            <div className="divide-y divide-surface-border">
              {history.map((item) => (
                <div
                  key={item.id}
                  className="hover:bg-surface-subtle/50 flex items-center justify-between p-3.5"
                >
                  <div className="flex items-center gap-3">
                    <FileSpreadsheet className="h-5 w-5 text-emerald-500" />
                    <div>
                      <div className="text-xs font-semibold text-content-main">
                        {item.filename}
                      </div>
                      <div className="text-2xs text-content-muted">
                        {item.marketplace.toUpperCase()} • {item.total_rows}{' '}
                        linhas •{' '}
                        {new Date(item.created_at).toLocaleDateString('pt-BR')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(item.net_profit)}
                      </div>
                      <div className="text-2xs text-content-muted">
                        Lucro Líquido
                      </div>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleSelectHistoryItem(item.id)}
                        leftIcon={<Eye className="h-3.5 w-3.5" />}
                      >
                        Ver
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setItemToDelete(item.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal de Exclusão de Histórico */}
      <ConfirmationModal
        isOpen={!!itemToDelete}
        title="Excluir Relatório de Conciliação"
        message="Deseja realmente remover este lote de conciliação financeira? As métricas consolidadas associadas serão excluídas."
        confirmLabel="Excluir Relatório"
        variant="danger"
        isLoading={isDeleting}
        onConfirm={confirmDeleteReconciliation}
        onCancel={() => setItemToDelete(null)}
      />
    </div>
  );
};
